import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { JSX, memo } from 'react';
import { getLocalTimeZone, parseDate, parseTime, today } from '@internationalized/date';

import { TmdxDatepicker, TmdxTimeInput } from '@ds';
import cn from 'classnames';
import { FormRow } from '../FormRow';
import { getInputValidationErrorProps } from '@/utils/helpers';

import {
  DateTimeChangeHandler,
  DateTimePickerInputsProps,
  ErrorContext,
  PickerDateValue,
  PickerTimeValue,
} from './types';
import { buildUtcDate, buildZonedValue, formatZonedTime, getDstCorrectedTime, isLessThanSixHours } from './utils';
import { ShortWindowBanner } from './ShortWindowBanner';
import { TimezoneDisplay } from './TimezoneDisplay';

export const DateTimePickerInputs = memo(
  <T extends FieldValues>({
    control,
    title,
    dateFieldName,
    dateFieldLabel,
    timeFieldName,
    timeFieldLabel,
    timeZoneFieldName,
    timeZoneFieldLabel,
    disabledDate,
    disabledTime,
    disabledPast = true,
    hasPermission,
    withShortWindowInfoBanner,
    displayLabels = false,
    multiRow = false,
    isRequired,
    dateFieldWidthClass,
    timeFieldWidthClass,
    timeZoneFieldWidthClass,
  }: DateTimePickerInputsProps<T>) => {
    const {
      trigger,
      getValues,
      setValue,
      formState: { errors },
    } = useFormContext();

    const dateStr = getValues(dateFieldName);
    const timeStr = getValues(timeFieldName);
    const timezone = getValues(timeZoneFieldName);
    const date = buildUtcDate(dateStr, timeStr, timezone);

    const showShortWindowBanner = withShortWindowInfoBanner && isLessThanSixHours(dateStr, timeStr, timezone);

    const dateErrorFromContext: ErrorContext = errors[dateFieldName];
    const timeErrorFromContext: ErrorContext = errors[timeFieldName];

    const correctTime = (dateValue: string) => {
      const corrected = getDstCorrectedTime(dateValue, getValues(timeFieldName), timezone);
      if (corrected) setValue(timeFieldName, corrected);
    };

    const handleDateChange = (v: PickerDateValue, onChange: DateTimeChangeHandler) => {
      const newDate = v ? v.toString() : null;
      onChange(newDate);
      if (newDate) correctTime(newDate);
      trigger(timeFieldName);
    };

    const handleTimeChange = (v: PickerTimeValue, onChange: DateTimeChangeHandler) => {
      if (!v) {
        onChange(null);
        trigger(dateFieldName);
        return;
      }
      onChange(formatZonedTime(v));
      trigger(dateFieldName);
    };

    const flexClass = multiRow ? 'flex-1' : 'flex-[50%]';
    const hasCustomWidths = !!(dateFieldWidthClass || timeFieldWidthClass || timeZoneFieldWidthClass);
    const rowWrapperClass = hasCustomWidths ? 'flex-none' : flexClass;
    const dateFieldClass = dateFieldWidthClass ? cn(dateFieldWidthClass, 'shrink-0') : flexClass;
    const timeFieldClass = timeFieldWidthClass ? cn(timeFieldWidthClass, 'shrink-0') : 'w-[90px]';
    const timeZoneFieldClass = cn(
      'truncate',
      timeZoneFieldWidthClass ? cn(timeZoneFieldWidthClass, 'shrink-0') : 'flex-1',
    );

    return (
      <>
        <FormRow
          title={title}
          multiRow={multiRow}
          spitRows={multiRow && !title}
          isRequired={isRequired}
          contentClass={hasCustomWidths ? 'flex-none' : undefined}
        >
          <div className={dateFieldClass}>
            <Controller
              name={dateFieldName as Path<T>}
              control={control}
              render={({ field: { value, onChange, ...field }, fieldState }) => (
                <TmdxDatepicker
                  isDisabled={disabledDate || !hasPermission}
                  aria-label={dateFieldLabel}
                  label={displayLabels ? dateFieldLabel : null}
                  minValue={disabledPast ? today(getLocalTimeZone()) : undefined}
                  value={value ? parseDate(value) : null}
                  {...getInputValidationErrorProps(fieldState, dateErrorFromContext)}
                  onChange={(v) => handleDateChange(v, onChange)}
                  {...field}
                />
              )}
            />
          </div>
          <div className={cn('flex gap-2', rowWrapperClass)}>
            <div className={timeFieldClass}>
              <Controller
                name={timeFieldName as Path<T>}
                control={control}
                render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => {
                  const zonedTimeValue = buildZonedValue(dateStr, value, timezone);
                  return (
                    <TmdxTimeInput
                      isInvalid={invalid || !!timeErrorFromContext}
                      errorMessage={error?.message || timeErrorFromContext?.message}
                      isDisabled={disabledTime || !hasPermission}
                      aria-label={timeFieldLabel}
                      hideTimeZone
                      label={displayLabels ? timeFieldLabel : null}
                      hourCycle={24}
                      value={zonedTimeValue ?? (value ? parseTime(value) : null)}
                      onChange={(v) => handleTimeChange(v as PickerTimeValue, onChange)}
                      {...field}
                    />
                  );
                }}
              />
            </div>
            <div className={timeZoneFieldClass}>
              <Controller
                name={timeZoneFieldName as Path<T>}
                control={control}
                render={({ field: { value } }) => (
                  <TimezoneDisplay
                    value={value}
                    date={date}
                    displayLabels={displayLabels}
                    timeZoneFieldLabel={timeZoneFieldLabel}
                  />
                )}
              />
            </div>
          </div>
        </FormRow>
        {showShortWindowBanner && <ShortWindowBanner />}
      </>
    );
  },
) as <T extends FieldValues>(props: DateTimePickerInputsProps<T>) => JSX.Element;
